import { useState } from 'react'
import PropTypes from 'prop-types'

import Auth from '../../../ui/modules/auth'

import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from '@mui/material'
import LoadingButton from '@mui/lab/LoadingButton'

import { ReactComponent as Eye } from '../../../assets/icons/eye.svg'
import { ReactComponent as EyeClosed } from '../../../assets/icons/eye-closed.svg'
import { ReactComponent as ArrowRight } from '../../../assets/icons/arrow-right.svg'
import { ReactComponent as WarningCircle } from '../../../assets/icons/warning-circle.svg'

import { makeFormPropTypes } from '../../../common/formikPropTypes'
import { loginPropTypes } from '../../../common/propTypes'
import useTranslations from '../../../i18n/useTranslations'

import styles from './styles'

const Login = ({
  onClickRequestPasswordReset,
  values,
  fieldErrors,
  formErrors,
  touched,
  onChange,
  onBlur,
  onSubmit,
  isLoggingIn,
}) => {
  const t = useTranslations()

  const [showPassword, setShowPassword] = useState(false)
  const isSubmitBtnDisabled =
    formErrors?.length !== 0 || Object.entries(fieldErrors)?.length !== 0

  const hasFormErrors = formErrors && formErrors.length > 0

  return (
    <Auth title={t('login:title')} onSubmit={onSubmit}>
      <Grid item>
        <Box sx={styles.fields}>
          <TextField
            id="user"
            label={t('noun:user')}
            autoComplete="user"
            error={hasFormErrors}
            helperText=" "
            value={values.user}
            onChange={onChange}
            onBlur={onBlur}
            className="input-full"
          />
          <TextField
            label={t('noun:password')}
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            error={hasFormErrors}
            helperText=" "
            value={values.password}
            onChange={onChange}
            onBlur={onBlur}
            InputProps={{
              endAdornment: (
                <InputAdornment
                  onClick={() => setShowPassword(showPassword => !showPassword)}
                  position="end"
                >
                  <IconButton
                    aria-label="toggle password visibility"
                    size="large"
                  >
                    {showPassword ? <Eye /> : <EyeClosed />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            className="input-full"
          />
        </Box>
        {hasFormErrors ? (
          <Alert severity="error" sx={styles.alert}>
            {formErrors.map(error => (
              <Typography key={`error-${error.key}`} variant="body2">
                {t(error.key, error.values)}
              </Typography>
            ))}
          </Alert>
        ) : null}
        <LoadingButton
          fullWidth
          type="submit"
          endIcon={<ArrowRight />}
          sx={styles.submitButton}
          loading={isLoggingIn}
          disabled={isSubmitBtnDisabled}
          loadingIndicator={
            <CircularProgress sx={styles.circularProgress} size={16} />
          }
        >
          {t('action:enter')}
        </LoadingButton>
        <Box sx={styles.rememberPasswordWrapper}>
          <Link component="button" onClick={onClickRequestPasswordReset}>
            {t('login:recover-password')}
          </Link>
        </Box>
        <Box sx={styles.alertWrapper}>
          <Alert
            severity="info"
            sx={styles.alert}
            iconMapping={{
              info: <WarningCircle />,
            }}
          >
            <Typography>
              {t('login:create-account-explanation', {
                link: text => (
                  <Link
                    href="https://www.cttexpress.com/hazte-cliente/"
                    target="_blank"
                  >
                    {text}
                  </Link>
                ),
                strong: text => <strong>{text}</strong>,
              })}
            </Typography>
          </Alert>
        </Box>
      </Grid>
    </Auth>
  )
}

Login.propTypes = {
  ...makeFormPropTypes(loginPropTypes),
  isLoggingIn: PropTypes.bool,
  onClickRequestPasswordReset: PropTypes.func,
}

export default Login
